import * as React from 'react';
import styled from 'styled-components';

import Typography from '@components/basics/typography';
import Flex from '@components/layout/flex';

import { InviteStatus } from '@common/types/invites/invite-status';
import { TracInvite } from '@services/invites/types/trac-invite';
import { TracInviteTypes } from '@services/invites/types/invite-types';

import MenuDropdown from '@webapp/controllers/form/menu-dropdown';
import { Translate } from 'react-localize-redux';
import AppState from '@webapp/store/state';
import SimpleUser from '@common/types/user/simple-user';
import { DialogConfiguration } from '@webapp/types/ui/dialog-configuration';
import { ButtonType } from '@components/actions/button/index';
import { connect, useDispatch, useSelector } from 'react-redux';
import { CreateDialog } from '../../../store/ui/action-creators';
import MotionOpacity from '@components/motion/motion-opacity';
import { Dispatch } from 'redux';
import {
  RemoveFromArtistTeam,
  SwitchArtistTeamRole,
} from '@webapp/store/artist/action-creators';
import { GenericDropdownBuilder } from '@webapp/controllers/form/dropdown';
import { RoleOptions } from '@webapp/pages/artist-profile/artist-team/invite-team-member-list';
import { ArtistRelation } from '@common/types/artist/artist-relation';
interface ArtistTeamItemProps {
  artistId?: string;
  className?: string;
  label?: string;
  status?: InviteStatus;
  actions?: React.ReactElement[];
  type?: TracInviteTypes;
  role?: ArtistRelation['role'];
  teamMember: SimpleUser;
  teamMemberList: SimpleUser[];
  confirmRemoveManager?: boolean;
  confirmRemoveMember?: boolean;
  SetConfirmRemoveManager: React.Dispatch<React.SetStateAction<boolean>>;
  SetConfirmRemoveMember: React.Dispatch<React.SetStateAction<boolean>>;
  SetSelectedmember: React.Dispatch<
    React.SetStateAction<SimpleUser | undefined>
  >;
  handleMenu: (action: string, invite: TracInvite) => void;
  openModal: (trackId: string, callback: (value: any) => void) => CreateDialog;
  switchTeamMemberRole: (
    artistId: string,
    teamMemberId: string,
    role: ArtistRelation['role']
  ) => SwitchArtistTeamRole;
}
const mapStateToProps = (state: AppState) => {
  return {
    artistId: state.artist.active?.ownerId,
  };
};
const buildDialog = (
  callback: (value: any) => void
): DialogConfiguration<ButtonType> => {
  return {
    title: 'Are you sure you want to delete this song?',
    text: "This can't be undone.",
    actions: [
      { type: 'primary', label: 'No', value: false },
      { type: 'secondary', label: 'Yes', value: true },
    ],
    callback,
  };
};
const mapDispatchToProps = (dispatch: Dispatch) => ({
  openModal: (trackId: string, callback: (value: any) => void) =>
    dispatch(new CreateDialog(trackId, buildDialog(callback))),
  switchTeamMemberRole: (
    artistId: string,
    teamMemberId: string,
    role: ArtistRelation['role']
  ) => dispatch(new SwitchArtistTeamRole(artistId, teamMemberId, role)),
});

const RoleDropdown = GenericDropdownBuilder<ArtistRelation['role']>();

export const ArtistTeamItem: React.FC<ArtistTeamItemProps> = (props) => {
  const dispatch = useDispatch();
  const [showManagerRemove, setShowManagerRemove] = React.useState(
    props.confirmRemoveManager
  );
  const [showMemberRemove, setShowMemberRemove] = React.useState(
    props.confirmRemoveMember
  );
  const [role, setRole] = React.useState<ArtistRelation['role']>(props.role);

  const activeArtistId = useSelector(
    (state: AppState) => state.artist.active?.id
  );

  const getAction = () => {
    const options = [{ label: 'Remove', value: 'edit' }];
    return options;
  };

  const getOwner = () => {
    let owner = '';
    if (props.artistId === props.teamMember.id) {
      owner = '(You)';
    }
    return owner;
  };
  const isSamePercentage = () => {
    let flag = false;
    if (
      props.teamMember?.split?.recordingOwnership ===
      props.teamMember?.split?.merchOwnership
    ) {
      flag = true;
    }
    return flag;
  };
  const handleRemove = (role: 'manager' | 'member') => {
    if (role === 'manager') {
      setShowManagerRemove(true);
    } else {
      setShowMemberRemove(true);
    }
  };

  const handleRoleChange = (role: ArtistRelation['role']) => {
    if (props.teamMember.id && activeArtistId) {
      setRole(role);
      props.switchTeamMemberRole(activeArtistId, props.teamMember.id, role);
      return;
    }
  };

  return (
    <Wrapper>
      <Flex
        justifyContent="space-between"
        className={`${props.className} item-top-radius relative-position`}
      >
        <Flex
          className="text-container"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Typography className="team-label" weight="700">
            {props.label} {getOwner()}
          </Typography>
        </Flex>
        {props.teamMember.role === 'manager' &&
          props.teamMemberList.length > 1 && (
            <Flex
              className="action"
              justifyContent="flex-end"
              alignItems="center"
            >
              <MenuButton>
                <MenuDropdown
                  isSmall
                  hasDottedIcon
                  menuContent={getAction()}
                  onSelect={() => {
                    handleRemove('manager');
                  }}
                />
              </MenuButton>
            </Flex>
          )}
        {props.teamMember.role === 'member' &&
          props.teamMemberList.length > 1 && (
            <Flex
              className="action"
              justifyContent="flex-end"
              alignItems="center"
            >
              <MenuButton>
                <MenuDropdown
                  isSmall
                  hasDottedIcon
                  menuContent={getAction()}
                  onSelect={() => {
                    handleRemove('member');
                  }}
                />
              </MenuButton>
            </Flex>
          )}
      </Flex>
      {showManagerRemove && (
        <MotionOpacity>
          <RemoveWrapper>
            <Typography>
              <Translate id="ARTIST_PROFILE.TEAM.INPUT.REMOVE" />
            </Typography>
            <Flex justifyContent="flex-end" alignItems="center" gap={8}>
              <Link
                onClick={() => {
                  props.SetConfirmRemoveManager(true);
                  props.SetSelectedmember(props.teamMember);
                }}
              >
                <Translate id="ARTIST_PROFILE.TEAM.INPUT.REMOVE.YES" />
              </Link>
              <Link onClick={() => setShowManagerRemove(false)}>
                <Translate id="ARTIST_PROFILE.TEAM.INPUT.REMOVE.NO" />
              </Link>
            </Flex>
          </RemoveWrapper>
        </MotionOpacity>
      )}
      {showMemberRemove && (
        <MotionOpacity>
          <RemoveWrapper>
            <Typography>
              <Translate id="ARTIST_PROFILE.TEAM.INPUT.REMOVE" />
            </Typography>
            <Flex justifyContent="flex-end" alignItems="center" gap={8}>
              <Link
                onClick={() => {
                  props.SetConfirmRemoveMember(true);
                  props.SetSelectedmember(props.teamMember);
                  activeArtistId &&
                    dispatch(
                      new RemoveFromArtistTeam(
                        activeArtistId,
                        props.teamMember.id
                      )
                    );
                }}
              >
                <Translate id="ARTIST_PROFILE.TEAM.INPUT.REMOVE.YES" />
              </Link>
              <Link onClick={() => setShowMemberRemove(false)}>
                <Translate id="ARTIST_PROFILE.TEAM.INPUT.REMOVE.NO" />
              </Link>
            </Flex>
          </RemoveWrapper>
        </MotionOpacity>
      )}
      {props.role && props.artistId !== props.teamMember.id ? (
        <RoleDropdown
          className={
            props.teamMemberList.some((member) => member.role === 'manager')
              ? 'dropdown-disabled'
              : ''
          }
          title={<Translate id="ARTIST_PROFILE.TEAM.INPUT.DROPDOWN.LABEL" />}
          value={role}
          disabled={props.teamMemberList.some(
            (member) => member.role === 'manager'
          )}
          parentRelative={true}
          options={RoleOptions}
          onChange={handleRoleChange}
        />
      ) : null}

      {/* Info */}
      {props.role?.toLowerCase() === 'manager' && (
        <>
          {props.teamMember?.split && (
            <Wrapper className={`mt-2 item-info ${props.className}`}>
              {isSamePercentage() ? (
                <Translate
                  id={'ARTIST_PROFILE.TEAM.INFO.SAME'}
                  data={{
                    NAME: props.label,
                    MASTER: props.teamMember?.split?.recordingOwnership,
                  }}
                />
              ) : (
                <Translate
                  id={'ARTIST_PROFILE.TEAM.INFO.DIFFERENT'}
                  data={{
                    NAME: props.label,
                    MASTER: props.teamMember?.split?.recordingOwnership,
                    MERCH: props.teamMember?.split?.merchOwnership,
                  }}
                />
              )}
            </Wrapper>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(ArtistTeamItem)`
  background: ${({ theme }) => theme.colors.differentBackground};
  border-radius: 4px;
  padding: 0 10px;
  height: 48px;
  user-select: none;

  @media ${({ theme }) => theme.device.laptopL} {
    height: 60px;
    padding: 0 20px;
  }

  .text-container {
    overflow: hidden;
  }

  .mt-2 {
    margin-top: 2px;
  }

  .team-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${({ theme }) => theme.colors.secondaryDark};
  }

  .action {
    @media ${({ theme }) => theme.device.laptopS} {
      pointer-events: none;
      width: 0;
      white-space: nowrap;
      transition: opacity
        ${({ theme }) =>
          `${theme.transitions.duration} ${theme.transitions.type}`};
    }

    .action-item {
      opacity: 0.5;
      transition: opacity
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`},
        color
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`};
      margin-left: 10px;

      @media ${({ theme }) => theme.device.tablet} {
        margin-left: 20px;
      }

      &:hover {
        opacity: 1;
      }

      &:first-child {
        margin-left: 0;
      }
    }
  }

  &:hover {
    .action {
      @media ${({ theme }) => theme.device.laptopS} {
        pointer-events: initial;
        width: auto;
        opacity: 1;
      }
    }
  }
`);

const MenuButton = styled.button`
  background-color: ${({ theme }) => theme.colors.differentBackground};
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  position: relative;
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondaryHover};
  }

  svg {
    width: 32px;
    height: 32px;
  }
`;
const Wrapper = styled.div`
  position: relative;
  top: 20px;
`;

const RemoveWrapper = styled.div`
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 0px 0px 4px 4px;
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.accentLight};

  @media ${({ theme }) => theme.device.laptopL} {
    padding: 10px 20px;
  }

  span {
    font-size: ${({ theme }) => theme.typography.small};
    font-weight: 700;
  }
`;
const Link = styled(Typography)`
  cursor: pointer;
  opacity: 1;
  transition: opacity
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover {
    opacity: 0.5;
  }
`;
